import Phaser from 'phaser';

import { TwentyOneMainScene } from '../../scenes/twentyone-main-scene';
import { Subscription } from 'rxjs';
import { ResizeMetrics } from '../../model/resize-metrics';
import { TwentyOne, GameEvent } from 'game-logic';
import { UxMetrics } from '../../model/ux-metrics';
import { ColumnStackViewModel } from './column-stack-view-model';
import { VerticalCardStackView } from './vertical-card-stack-view';
import { Positioning } from '../../model/positioning';
import { ChevronHintView } from './chevron-hint-view';
// import { DebugContainerVisual } from '../debug/debug-container-visual';

export class ColumnStackView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;

  private columnBoxFillImage: Phaser.GameObjects.Image;
  private clickRectangle: Phaser.GameObjects.Rectangle;
  private chevronHintView: ChevronHintView;
  private cardStackView: VerticalCardStackView;

  private gameInPlay = false;
  private pointerWasDown = false;
  private fadeValue = 0;

  constructor(
    scene: TwentyOneMainScene,
    x: number,
    y: number,
    private viewModel: ColumnStackViewModel,
  ) {
    super(scene, x, y);

    // new DebugContainerVisual(scene, this);

    this.subscriptionGroup = new Subscription();

    this.columnBoxFillImage = scene.add
      .image(0, 0, viewModel.columnBoxFillAsset.type)
      .setOrigin(0.5, 0.5)
      .setAlpha(0);
    this.add(this.columnBoxFillImage);

    this.clickRectangle = scene.add
      .rectangle(0, 0, 0, 0, 0x000000)
      .setAlpha(0.0)
      .setOrigin(0.5, 0.5);
    this.add(this.clickRectangle);

    this.chevronHintView = new ChevronHintView(scene, 0, 0, viewModel.chevronHintViewModel);
    this.add(this.chevronHintView);

    this.cardStackView = new VerticalCardStackView(scene, 0, 0, viewModel.cardStackViewModel);
    this.add(this.cardStackView);

    this.setName(ColumnStackView.name);
    scene.add.existing(this);

    this.handleInput();

    this.setupProgress(0);

    this.subscriptionGroup.add(
      this.viewModel.onGameSetupStart$.subscribe(() => {
        this.scene.tweens.add({
          targets: this,
          fadeValue: 1,
          duration: UxMetrics.LoadingFade,
          ease: Phaser.Math.Easing.Quadratic.InOut,
          onUpdate: (_tween) => {
            this.setupProgress(this.fadeValue);
          },
        });
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.onGameSetupComplete$.subscribe(() => {
        this.viewModel.enableInput(true);
      }),
    );
    this.subscriptionGroup.add(
      viewModel.onResetGameScene$.subscribe(() => {
        this.setupProgress(0);
        this.chevronHintView.hideChevrons(false);
        this.viewModel.enableInput(false);
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.currentResizeMetrics = resizeMetrics;

    this.chevronHintView?.onResize(resizeMetrics);
    this.cardStackView?.onResize(resizeMetrics);
    this.refreshLayout(resizeMetrics);
  }

  private setupProgress(_progress: number): void {
    // nothing
  }

  private refreshLayout(resizeMetrics: ResizeMetrics): void {
    const columnStackRect = Positioning.localColumnStackRect(
      resizeMetrics.layoutConfig,
      resizeMetrics.scalingMetrics,
    );

    this.setSize(columnStackRect.width, columnStackRect.height);
    this.columnBoxFillImage.setDisplaySize(columnStackRect.width, columnStackRect.height);
    this.clickRectangle.setSize(columnStackRect.width, columnStackRect.height);

    this.chevronHintView.setPosition(0, 0);

    this.cardStackView.setPosition(
      0,
      -resizeMetrics.layoutConfig.paddingXsm * resizeMetrics.scalingMetrics.scaleFactor,
    );

    this.setInteractive({
      hitArea: this.clickRectangle,
      hitAreaCallback: (rect: Phaser.Geom.Rectangle, x: number, y: number) =>
        Phaser.Geom.Rectangle.Contains(rect, x, y),
    });
  }

  public async handleLogicEvents(event: GameEvent<TwentyOne.TwentyOneEvents>): Promise<void> {
    switch (event.type) {
      case 'onRoundStateChanged':
        {
          this.gameInPlay = event.payload.state === TwentyOne.RoundState.Play;
        }
        break;
      case 'onBeforeMoveToColumn':
        this.chevronHintView.hideChevrons(event.instant);
        break;
      case 'onMoveCurrentToColumn':
        {
          const column = event.payload.cardColumn;
          if (column.pileId === this.viewModel.columnId) {
            await this.cardStackView.handleLogicEvents(event);
          }
        }
        break;
      case 'onColumnCleared':
        {
          const column = event.payload.cardColumn;
          if (column.pileId === this.viewModel.columnId) {
            await this.cardStackView.handleLogicEvents(event);
          }
        }
        break;
      case 'onDealComplete':
        {
          const column = event.payload.cardColumns.get(this.viewModel.columnId);
          if (column) {
            this.chevronHintView.activateChevrons(true, event.instant);
          }
        }
        break;
      case 'onAfterMoveToColumn':
        {
          // Show chevrons
          const column = event.payload.cardColumns.get(this.viewModel.columnId);
          if (column) {
            const currentCard = event.payload.cardStackCurrent;
            if (currentCard.containsWild) {
              this.chevronHintView.activateChevrons(false, event.instant);
              return;
            }
            const scoreHint = column.calculateScoreHint(currentCard.allFaceCards);
            const nextMoveIsBust = TwentyOne.TwentyOneScoring.hasBust(scoreHint);

            if (nextMoveIsBust) {
              this.chevronHintView.hideChevrons(event.instant);
            } else {
              this.chevronHintView.activateChevrons(false, event.instant);
            }
          }
        }
        break;
      default:
        break;
    }
  }

  private handleInput() {
    this.on(Phaser.Input.Events.POINTER_DOWN, (_pointer: Phaser.Input.Pointer) => {
      this.pointerWasDown = true;
    });

    this.on(Phaser.Input.Events.POINTER_UP, (_pointer: Phaser.Input.Pointer) => {
      if (this.pointerWasDown && this.viewModel.canClick && this.gameInPlay) {
        this.viewModel.performClick();
        this.animateFill();
      }
      this.pointerWasDown = false;
    });

    this.on(Phaser.Input.Events.POINTER_OUT, (_pointer: Phaser.Input.Pointer) => {
      if (this.pointerWasDown) {
        this.pointerWasDown = false; // Cancel the tap if moved outside
      }
    });
  }

  private animateFill() {
    this.scene.tweens.add({
      targets: this.columnBoxFillImage,
      alpha: { from: 0, to: 1 },
      duration: UxMetrics.ColumnFlashIn,
      ease: Phaser.Math.Easing.Sine.In,
    });

    this.scene.tweens.add({
      targets: this.columnBoxFillImage,
      alpha: { from: 1, to: 0 },
      duration: UxMetrics.ColumnFlashOut,
      delay: UxMetrics.ColumnFlashIn,
      ease: Phaser.Math.Easing.Sine.Out,
    });
  }
}
